import { useEffect, useRef, type ReactElement } from 'react';
import { Link } from 'react-router';
import type { AutopilotMode } from '@zenda/api-contract';
import { Icon } from './Icon';
import { useTip } from './Tip';
import { useT } from '../lib/i18n';
import { dict } from '../lib/dict';
import { tocar } from '../lib/aviso';
import { useConversations } from '../lib/queries';
import { useMudarModo, usePilotoResumo } from '../lib/piloto';
import '../styles/piloto.css';

const MODOS: readonly AutopilotMode[] = ['off', 'copiloto', 'piloto'];

/**
 * Aviso do sistema quando o contador sobe com a aba em segundo plano (plano §6.4). Sem
 * nome de paciente: a notificação aparece na tela de bloqueio, e a regra de `aviso.ts`
 * vale aqui também.
 */
function avisarPrecisa(medico: string): void {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return;
  try {
    const n = new Notification(dict().piloto.estados.needs_human, {
      body: dict().piloto.notificacao(medico),
      tag: 'zenda-piloto',
    });
    n.onclick = () => {
      window.focus();
      window.location.assign('/inbox?filtro=precisa');
      n.close();
    };
  } catch {
    // Aviso é acessório.
  }
}

/**
 * O controle de três posições e o contador (plano §3), numa linha do cartão da atendente:
 * a barra lateral é curta, e um bloco só para o piloto empurrava o resto para baixo.
 */
export function PilotoNaBarra({ recolhida }: { readonly recolhida: boolean }): ReactElement | null {
  const t = useT();
  const tip = useTip();
  const resumo = usePilotoResumo();
  const mudar = useMudarModo();
  const conversas = useConversations();
  const antes = useRef<number | null>(null);

  const precisa = resumo.data?.needsYou ?? 0;
  useEffect(() => {
    if (antes.current !== null && precisa > antes.current) {
      const ultima = [...(conversas.data ?? [])]
        .filter((c) => c.autopilot.state === 'needs_human')
        .sort((a, b) =>
          (b.autopilot.changedAt ?? '').localeCompare(a.autopilot.changedAt ?? ''),
        )[0];
      if (ultima !== undefined) avisarPrecisa(ultima.doctorName);
      if (resumo.data?.sound === true) tocar();
    }
    antes.current = precisa;
  }, [precisa]);

  if (resumo.data === undefined) return null;
  const r = resumo.data;
  const total = r.needsYou + r.takenOver;
  const pausa = r.pause === null || r.mode === 'off' ? null : t.piloto.pausas[r.pause];
  const conta =
    total === 0 ? null : (
      <Link
        to="/inbox?filtro=precisa"
        className="piloto-who__conta num"
        aria-label={t.piloto.precisam(total)}
        {...tip(t.piloto.precisam(total))}
      >
        {total}
      </Link>
    );

  if (recolhida) {
    return (
      <div className="piloto-who piloto-who--mini">
        <span
          className={`piloto-who__icone${pausa === null ? '' : ' is-pausado'}`}
          {...tip(
            `${t.piloto.titulo}: ${t.piloto.modos[r.mode]}${pausa === null ? '' : ` · ${pausa}`}`,
          )}
        >
          <Icon name="robot" size={15} />
        </span>
        {conta}
      </div>
    );
  }

  return (
    <div className="piloto-who">
      <div
        className={`piloto-modos${pausa === null ? '' : ' is-pausado'}`}
        role="radiogroup"
        aria-label={pausa === null ? t.piloto.titulo : `${t.piloto.titulo} · ${pausa}`}
      >
        {MODOS.map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={r.mode === m}
            className={`piloto-modos__item${r.mode === m ? ' is-on' : ''} piloto-modos__item--${m}`}
            disabled={mudar.isPending}
            onClick={() => mudar.mutate(m)}
            {...tip(
              r.mode === m && pausa !== null
                ? `${t.piloto.titulo}: ${pausa}`
                : `${t.piloto.titulo} · ${t.piloto.modos[m]}: ${t.piloto.modosLongos[m]}`,
            )}
          >
            {t.piloto.modosCurtos[m]}
          </button>
        ))}
      </div>
      {conta}
    </div>
  );
}
